import type { Selection } from "../state/ui";

/** Arbitrage des colonnes de la carte (R7, R16 : « elles s'arbitrent au lieu de se chevaucher ») : la colonne
 *  gauche (vue courante, puis Calques) et la colonne droite (fiche d'objet ou assistant IA) ne se recouvrent
 *  jamais, et la carte garde une zone utile entre elles.
 *
 *  - `row` : vue et Calques côte à côte (rien d'ouvert à droite, ou assez de place pour garder une carte utile
 *    entre les deux colonnes ; Calques repliés, une simple barre en haut, il suffit qu'ils ne touchent pas la
 *    colonne droite) ;
 *  - `stack` : Calques sous la vue (fenêtre étroite, ou trop peu de carte libre côte à côte) ;
 *  - `hidden` : colonne gauche masquée tant que la droite est occupée, faute de place pour la vue et une carte
 *    utile (tablette en portrait, 1 024 px avec la barre latérale dépliée).
 *  Un seul seuil de carte libre (`MIN_FREE_MAP_PX`) départage les trois. */
export type LeftColumnLayout = "row" | "stack" | "hidden";

/** Marges et largeurs (px) : panneaux à 12 px des bords et entre eux, vue 380, Calques 300, fiche 340, assistant
 *  360 (`PANEL_WIDTH`). */
const EDGE = 12;
const VIEW = 380;
const LAYERS = 300;
const CARD = 340;
const ASSISTANT = 360;
/** Largeur de carte laissée libre au moins entre les deux colonnes pour qu'elle reste utilisable. */
export const MIN_FREE_MAP_PX = 140;

/** Largeur de `main` en deçà de laquelle vue et Calques (repliés) touchent un panneau de droite
 *  (12 + 380 + 12 + 300 + 12 + 360 + 12 ≈ 1 090 px avec l'assistant). */
export const ROW_MIN_PX = EDGE + VIEW + EDGE + LAYERS + EDGE + ASSISTANT + EDGE;

/** Carte libre (px) entre la colonne gauche (vue, et Calques s'ils sont à côté) et le panneau de droite. */
export function freeMapPx(mainWidth: number, right: "card" | "assistant", withLayers: boolean): number {
  const leftWidth = EDGE + VIEW + EDGE + (withLayers ? LAYERS + EDGE : 0);
  const rightWidth = (right === "assistant" ? ASSISTANT : CARD) + EDGE;
  return mainWidth - leftWidth - rightWidth;
}

export function leftColumnLayout({
  mainWidth,
  narrow,
  right,
  layersOpen = true,
}: {
  /** Largeur de la zone de la carte (`main`), en pixels ; 0 tant qu'elle n'est pas mesurée. */
  mainWidth: number;
  /** Fenêtre sous 1 024 px (`max-lg`) : Calques toujours sous la vue. */
  narrow: boolean;
  /** Ce qui occupe la colonne droite. */
  right: "card" | "assistant" | null;
  /** Calques dépliés (panneau haut) ; repliés, ils ne sont qu'une barre en haut de la colonne. */
  layersOpen?: boolean;
}): LeftColumnLayout {
  if (!right) return narrow ? "stack" : "row";
  if (narrow) return "hidden";
  if (mainWidth <= 0) return "row";
  if (layersOpen ? freeMapPx(mainWidth, right, true) >= MIN_FREE_MAP_PX : mainWidth >= ROW_MIN_PX) return "row";
  return freeMapPx(mainWidth, right, false) >= MIN_FREE_MAP_PX ? "stack" : "hidden";
}

/** Clé d'un objet sélectionné (couche et identifiant) : une fiche par objet, qui entre et sort en glissé quand on
 *  passe d'un objet à l'autre. */
export function selectionKey(selection: Selection): string {
  if (selection.layer === "unit") return `unit:${selection.code}`;
  if (selection.layer === "subunit") return `subunit:${selection.key}`;
  if (selection.layer === "line") return `line:${selection.name}`;
  if (selection.layer === "graphic") return `graphic:${selection.id}`;
  if (selection.layer === "annotation") return `annotation:${selection.id}`;
  if (selection.layer === "measure") return `measure:${selection.key}:${selection.phase ?? ""}`;
  const id = selection.properties.id ?? selection.properties.key ?? selection.properties.osm_id;
  return `${selection.layer}:${id !== undefined && id !== null ? String(id) : selection.lngLat.join(",")}`;
}
